import React, { useState, useEffect, useContext } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Login } from '../../pages';
import { clearSessionToken, inputValidations, loadScriptByURL } from '../../utils';
import { useAuthService, AuthContext } from '../../hooks';
import { loaderService } from '../../services';

const recaptchaKey = '6LcgYzUeAAAAAMzXJ7ddC57PNtA0k-stcLituAZE';
const MIN_CAPTCHA_SCORE = 0.5;
const PHONE_LENGTH = 10;
const captchaError = 'No pudimos validar que no eres un robot. Intenta de nuevo.';
const sendCodeError = 'No pudimos enviar el código. Revisa que el número o correo esté registrado e intenta de nuevo.';

// Colombia y México usan 10 dígitos. Si pegan el número con indicativo, se lo quitamos.
const toLocalPhone = (input, countryCode) => {
  const digits = input.replace(/\D/g, '');
  const prefix = countryCode.replace('+', '');
  const local = digits.length > PHONE_LENGTH && digits.startsWith(prefix)
    ? digits.slice(prefix.length)
    : digits;
  return local.slice(0, PHONE_LENGTH);
};

function LoginContainer() {
  const [inputMode, setInputMode] = useState('telefono');
  const [countryCode, setCountryCode] = useState('+57');
  const [value, setValue] = useState('');
  const [errorMsg, setErrorMsg] = useState(null);
  const { loginService } = useAuthService();
  const { login, authUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const isPhone = inputMode === 'telefono';
  const isValidInput = isPhone
    ? value.length === PHONE_LENGTH
    : inputValidations.isEmailValid(value);
  const valor = isPhone ? `${countryCode}${value}` : value;

  useEffect(() => {
    loadScriptByURL(
      'recaptcha-key',
      `https://www.google.com/recaptcha/api.js?render=${recaptchaKey}`,
      () => {},
    );
  }, []);

  const onSubmit = async (captcha) => {
    loaderService.show();
    clearSessionToken();
    // Igual que trakku-admin y la app: el código del teléfono sale por WhatsApp y SMS queda como respaldo.
    const response = await loginService({
      captcha,
      valor,
      medio: isPhone ? 'whatsapp' : 'correo',
    });
    loaderService.hide();

    if (response?.uuid) {
      login(response.uuid);
      navigate('../verify', {
        replace: true,
        state: {
          uuid: response.uuid,
          captcha,
          valor,
          medio: inputMode,
        },
      });
      return;
    }
    const lowScore = typeof response?.score === 'number' && response.score < MIN_CAPTCHA_SCORE;
    setErrorMsg(lowScore ? captchaError : sendCodeError);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!isValidInput) return;
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(recaptchaKey, { action: 'submit' })
        .then(onSubmit);
    });
  };

  const onChangeInput = (event) => {
    const input = event.target.value;
    setValue(isPhone ? toLocalPhone(input, countryCode) : input.trim());
    setErrorMsg(null);
  };

  const onChangeMode = (mode) => {
    setInputMode(mode);
    setValue('');
    setErrorMsg(null);
  };

  const onChangeCountryCode = (code) => {
    setCountryCode(code);
    setErrorMsg(null);
  };

  // Con la sesión guardada no tiene sentido mostrar el login: se va directo al mapa.
  if (authUser?.authToken) return <Navigate to="/map" replace />;

  return (
    <Login
      value={value}
      errorMsg={errorMsg}
      inputMode={inputMode}
      countryCode={countryCode}
      isValidInput={isValidInput}
      handleSubmit={handleSubmit}
      onChangeMode={onChangeMode}
      onChangeInput={onChangeInput}
      onChangeCountryCode={onChangeCountryCode}
    />
  );
}

export default LoginContainer;
